import React, { FC } from 'react';
import { Box, Card, Grid, Tooltip, Typography } from '@mui/material';
import {
  Apartment as ApartmentIcon,
  AccountBalanceWallet as WalletIcon,
  Hotel as HotelIcon,
  FilterAlt as PipelineIcon,
  WarningAmber as WarningIcon,
  CalendarToday as CalendarIcon,
  Groups as GroupsIcon,
  EventAvailable as NewAccountIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { formatCompactIndianCurrency, formatNumber } from '../utils/formatters';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';

export interface ExecutiveSummaryData {
  companyCount?: number;
  miceRevenue?: number;
  roomNights?: number;
  pipelinePotential?: number;
  highPriorityAccounts?: number;
  meetings?: number;
  dormantAccounts?: number;
  newThisMonth?: number;
}

interface ExecutiveSummaryProps {
  summary: ExecutiveSummaryData | null;
  loading: boolean;
}

interface MetricCardConfig {
  label: string;
  value: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  tooltip: string;
}

const KpiMetricCard: FC<MetricCardConfig> = ({ label, value, icon, iconBg, iconColor, tooltip }) => (
  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.75,
      borderRadius: 2,
      border: '1px solid',
      borderColor: '#E5E7EB',
      backgroundColor: '#fff',
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      height: 84,
    }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: iconBg,
        color: iconColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& svg': { fontSize: 20 },
      }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontSize: '0.72rem',
          fontWeight: 500,
          display: 'block',
          mb: 0.25,
          textTransform: 'none',
          pr: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
    <Tooltip title={tooltip} arrow placement="top">
      <HelpOutlineIcon
        sx={{ position: 'absolute', top: 8, right: 8, fontSize: 14, color: 'text.disabled', cursor: 'help' }}
      />
    </Tooltip>
  </Box>
);

const ExecutiveSummary: FC<ExecutiveSummaryProps> = ({ summary, loading }) => {
  const currencySymbol = getCurrencySymbol();
  const displayValue = (formatter: () => string) => (loading ? '—' : formatter());

  const portfolioMetrics: MetricCardConfig[] = [
    {
      label: 'Companies',
      value: displayValue(() => formatNumber(summary?.companyCount ?? 0)),
      icon: <ApartmentIcon />,
      iconBg: '#DBEAFE',
      iconColor: '#2563EB',
      tooltip: 'Total number of companies in the selected executive’s portfolio.',
    },
    {
      label: 'MICE Revenue',
      value: displayValue(() => formatCompactIndianCurrency(summary?.miceRevenue, currencySymbol)),
      icon: <WalletIcon />,
      iconBg: '#E0F2FE',
      iconColor: '#0284C7',
      tooltip: 'Total MICE / event revenue booked across the portfolio for the period.',
    },
    {
      label: 'Room Nights',
      value: displayValue(() => formatNumber(summary?.roomNights ?? 0)),
      icon: <HotelIcon />,
      iconBg: '#D1FAE5',
      iconColor: '#059669',
      tooltip: 'Total room nights booked across the portfolio for the period.',
    },
    {
      label: 'Pipeline (Potential)',
      value: displayValue(() => formatCompactIndianCurrency(summary?.pipelinePotential, currencySymbol)),
      icon: <PipelineIcon />,
      iconBg: '#EDE9FE',
      iconColor: '#7C3AED',
      tooltip: 'Estimated revenue potential (room + event) across all companies in the portfolio.',
    },
  ];

  const accountMetrics: MetricCardConfig[] = [
    {
      label: 'High Priority Accounts',
      value: displayValue(() => formatNumber(summary?.highPriorityAccounts ?? 0)),
      icon: <WarningIcon />,
      iconBg: '#FFEDD5',
      iconColor: '#EA580C',
      tooltip: 'Companies classified as Category A (high priority).',
    },
    {
      label: 'Meetings Due',
      value: displayValue(() => formatNumber(summary?.meetings ?? 0)),
      icon: <CalendarIcon />,
      iconBg: '#DBEAFE',
      iconColor: '#2563EB',
      tooltip: 'Scheduled meetings / calls due for this executive in the period.',
    },
    {
      label: 'Dormant Accounts',
      value: displayValue(() => formatNumber(summary?.dormantAccounts ?? 0)),
      icon: <GroupsIcon />,
      iconBg: '#FEE2E2',
      iconColor: '#DC2626',
      tooltip: 'Accounts with no activity or engagement over an extended period.',
    },
    {
      label: 'New This Month',
      value: displayValue(() => formatNumber(summary?.newThisMonth ?? 0)),
      icon: <NewAccountIcon />,
      iconBg: '#D1FAE5',
      iconColor: '#059669',
      tooltip: 'Accounts added to the portfolio in the current month.',
    },
  ];

  const allMetrics = [...portfolioMetrics, ...accountMetrics];

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        border: '1px solid',
        borderColor: '#E5E7EB',
      }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          Executive Summary
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          Overview of your portfolio performance for the selected period.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {allMetrics.map(metric => (
          <Grid item xs={12} sm={6} md={3} key={metric.label}>
            <KpiMetricCard {...metric} />
          </Grid>
        ))}
      </Grid>
    </Card>
  );
};

export default ExecutiveSummary;
